import React, { useState, useEffect } from 'react';
import { Clock, Play, Square, Plus, Trash2, Calendar, DollarSign, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';

const HoursTracker = () => {
  const { currentUser } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [activeShift, setActiveShift] = useState(null);
  const [hourlyRate, setHourlyRate] = useState(15);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(getWeekStart(new Date()));
  const [manualEntry, setManualEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '17:00',
    breakMinutes: 30,
    notes: ''
  });

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff)).toISOString().split('T')[0];
  }

  // Load data from localStorage
  useEffect(() => {
    if (currentUser) {
      const savedShifts = localStorage.getItem(`shifts_${currentUser.uid}`);
      const savedRate = localStorage.getItem(`hourlyRate_${currentUser.uid}`);
      const savedActive = localStorage.getItem(`activeShift_${currentUser.uid}`);
      
      if (savedShifts) setShifts(JSON.parse(savedShifts));
      if (savedRate) setHourlyRate(parseFloat(savedRate));
      if (savedActive) setActiveShift(JSON.parse(savedActive));
    }
  }, [currentUser]);

  // Save shifts to localStorage
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(`shifts_${currentUser.uid}`, JSON.stringify(shifts));
    }
  }, [shifts, currentUser]);

  // Save hourly rate
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(`hourlyRate_${currentUser.uid}`, hourlyRate.toString());
    }
  }, [hourlyRate, currentUser]);

  // Save active shift
  useEffect(() => {
    if (currentUser) {
      if (activeShift) {
        localStorage.setItem(`activeShift_${currentUser.uid}`, JSON.stringify(activeShift));
      } else {
        localStorage.removeItem(`activeShift_${currentUser.uid}`);
      }
    }
  }, [activeShift, currentUser]);

  const clockIn = () => {
    const now = new Date();
    setActiveShift({
      id: Date.now(),
      startTime: now.toISOString(),
      date: now.toISOString().split('T')[0]
    });
  };

  const clockOut = () => {
    if (!activeShift) return;

    const endTime = new Date();
    const shift = {
      ...activeShift,
      endTime: endTime.toISOString(),
      breakMinutes: 0,
      notes: ''
    };

    setShifts(prev => [shift, ...prev]);
    setActiveShift(null);
  };

  const addManualShift = () => {
    const startDateTime = new Date(`${manualEntry.date}T${manualEntry.startTime}`);
    const endDateTime = new Date(`${manualEntry.date}T${manualEntry.endTime}`);

    if (endDateTime <= startDateTime) {
      alert('End time must be after start time');
      return;
    }

    const shift = {
      id: Date.now(),
      date: manualEntry.date,
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
      breakMinutes: parseInt(manualEntry.breakMinutes) || 0,
      notes: manualEntry.notes
    };

    setShifts(prev => [shift, ...prev].sort((a, b) => new Date(b.startTime) - new Date(a.startTime)));
    setShowAddModal(false);
    setManualEntry({
      date: new Date().toISOString().split('T')[0],
      startTime: '09:00',
      endTime: '17:00',
      breakMinutes: 30,
      notes: ''
    });
  };

  const deleteShift = (id) => {
    if (window.confirm('Delete this shift?')) {
      setShifts(prev => prev.filter(s => s.id !== id));
    }
  };

  const calculateHours = (shift) => {
    const start = new Date(shift.startTime);
    const end = new Date(shift.endTime);
    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);
    const breakHours = (shift.breakMinutes || 0) / 60;
    return Math.max(0, diffHours - breakHours);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Get shifts for selected week
  const getWeekShifts = () => {
    const weekStart = new Date(selectedWeek);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);

    return shifts.filter(shift => {
      const shiftDate = new Date(shift.date);
      return shiftDate >= weekStart && shiftDate < weekEnd;
    });
  };

  const weekShifts = getWeekShifts();
  const weekHours = weekShifts.reduce((sum, shift) => sum + calculateHours(shift), 0);
  const weekEarnings = weekHours * hourlyRate;

  // Calculate current shift duration
  const [currentDuration, setCurrentDuration] = useState('0h 0m');
  
  useEffect(() => {
    if (activeShift) {
      const interval = setInterval(() => {
        const start = new Date(activeShift.startTime);
        const now = new Date();
        const diffMs = now - start;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        setCurrentDuration(`${hours}h ${minutes}m`);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [activeShift]);

  const navigateWeek = (direction) => {
    const current = new Date(selectedWeek);
    current.setDate(current.getDate() + (direction * 7));
    setSelectedWeek(current.toISOString().split('T')[0]);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-brand-brown flex items-center">
              <Clock className="w-8 h-8 mr-3 text-pastel-blue" />
              Hours Tracker
            </h1>
            <p className="text-brand-brown/60 mt-1">Track your work hours & earnings</p>
          </div>
          <button
            onClick={() => setShowSettingsModal(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <DollarSign className="w-5 h-5 text-brand-brown/60" />
          </button>
        </div>

        {/* Clock In/Out Card */}
        <div className={`card p-6 mb-6 ${activeShift ? 'bg-gradient-to-r from-pastel-green/30 to-pastel-blue/30 border-2 border-pastel-green' : ''}`}>
          {activeShift ? (
            <div className="text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-green-600 font-medium">Currently Working</span>
              </div>
              <p className="text-4xl font-bold text-brand-brown mb-1">{currentDuration}</p>
              <p className="text-brand-brown/60 text-sm mb-4">
                Started at {formatTime(activeShift.startTime)}
              </p>
              <button
                onClick={clockOut}
                className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-xl font-bold flex items-center mx-auto transition-colors"
              >
                <Square className="w-5 h-5 mr-2" />
                Clock Out
              </button>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-brand-brown/60 mb-4">Ready to start your shift?</p>
              <button
                onClick={clockIn}
                className="bg-pastel-green hover:bg-green-400 text-white px-8 py-3 rounded-xl font-bold flex items-center mx-auto transition-colors"
              >
                <Play className="w-5 h-5 mr-2" />
                Clock In
              </button>
            </div>
          )}
        </div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigateWeek(-1)}
            className="px-3 py-1 text-brand-brown/60 hover:text-brand-brown"
          >
            ← Prev
          </button>
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-pastel-purple" />
            <span className="font-medium text-brand-brown">
              Week of {formatDate(selectedWeek)}
            </span>
          </div>
          <button
            onClick={() => navigateWeek(1)}
            className="px-3 py-1 text-brand-brown/60 hover:text-brand-brown"
          >
            Next →
          </button>
        </div>

        {/* Week Summary */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="card p-4 text-center">
            <p className="text-3xl font-bold text-pastel-purple">{formatHours(weekHours)}</p>
            <p className="text-sm text-brand-brown/60">This Week</p>
          </div>
          <div className="card p-4 text-center bg-gradient-to-r from-pastel-green/20 to-pastel-blue/20">
            <p className="text-3xl font-bold text-green-600">${weekEarnings.toFixed(2)}</p>
            <p className="text-sm text-brand-brown/60">Est. Earnings</p>
          </div>
        </div>

        {/* Add Manual Entry Button */}
        <button
          onClick={() => setShowAddModal(true)}
          className="w-full card p-3 mb-4 flex items-center justify-center text-brand-brown/60 hover:text-brand-brown hover:border-pastel-purple transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Manual Entry
        </button>

        {/* Shifts List */}
        <div className="space-y-2">
          <h3 className="font-semibold text-brand-brown mb-2">Shift History</h3>
          
          {weekShifts.length === 0 ? (
            <div className="card p-6 text-center">
              <Clock className="w-10 h-10 mx-auto text-brand-brown/20 mb-2" />
              <p className="text-brand-brown/60">No shifts this week</p>
            </div>
          ) : (
            weekShifts.map(shift => (
              <div key={shift.id} className="card p-3 flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium text-brand-brown">{formatDate(shift.date)}</p>
                  <p className="text-sm text-brand-brown/60">
                    {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                    {shift.breakMinutes > 0 && ` (${shift.breakMinutes}min break)`}
                  </p>
                  {shift.notes && (
                    <p className="text-xs text-pastel-purple mt-1">{shift.notes}</p>
                  )}
                </div>
                <div className="text-right mr-3">
                  <p className="font-bold text-pastel-purple">{formatHours(calculateHours(shift))}</p>
                  <p className="text-xs text-green-600">${(calculateHours(shift) * hourlyRate).toFixed(2)}</p>
                </div>
                <button
                  onClick={() => deleteShift(shift.id)}
                  className="p-2 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4 text-brand-brown/40 hover:text-red-500" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Add Manual Entry Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-brand-brown">Add Manual Entry</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">Date</label>
                  <input
                    type="date"
                    value={manualEntry.date}
                    onChange={(e) => setManualEntry(prev => ({ ...prev, date: e.target.value }))}
                    className="input w-full"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">Start Time</label>
                    <input
                      type="time"
                      value={manualEntry.startTime}
                      onChange={(e) => setManualEntry(prev => ({ ...prev, startTime: e.target.value }))}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">End Time</label>
                    <input
                      type="time"
                      value={manualEntry.endTime}
                      onChange={(e) => setManualEntry(prev => ({ ...prev, endTime: e.target.value }))}
                      className="input w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">Break (minutes)</label>
                  <input
                    type="number"
                    value={manualEntry.breakMinutes}
                    onChange={(e) => setManualEntry(prev => ({ ...prev, breakMinutes: e.target.value }))}
                    className="input w-full"
                    min="0"
                    step="5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">Notes (optional)</label>
                  <input
                    type="text"
                    value={manualEntry.notes}
                    onChange={(e) => setManualEntry(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="e.g., Covered for Sarah"
                    className="input w-full"
                  />
                </div>

                <button
                  onClick={addManualShift}
                  className="btn-primary w-full py-3"
                >
                  Add Shift
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {showSettingsModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-brand-brown">Settings</h2>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-1">
                  Hourly Rate ($)
                </label>
                <input
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                  className="input w-full"
                  min="0"
                  step="0.25"
                />
                <p className="text-xs text-brand-brown/60 mt-1">
                  Used to calculate estimated earnings
                </p>
              </div>

              <button
                onClick={() => setShowSettingsModal(false)}
                className="btn-primary w-full py-3 mt-4"
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HoursTracker;
